import type {
  Memo,
  CreateMemoDto,
  UpdateMemoDto,
} from "@repo/exit-calc-interfaces";
import { z } from "zod";
import type { HttpClient } from "../http";
import type { DataAndMessageResponse, MessageResponse } from "../types";

const MemoSchema: z.ZodType<Memo> = z.object({
  id: z.number(),
  userId: z.number(),
  title: z.string(),
  content: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const ListMemoResponseSchema: z.ZodType<DataAndMessageResponse<Memo[]>> =
  z.object({
    data: z.array(MemoSchema),
    message: z.string(),
  });

const GetMemoResponseSchema: z.ZodType<DataAndMessageResponse<Memo>> = z.object(
  {
    data: MemoSchema,
    message: z.string(),
  },
);

const CreateMemoResponseSchema: z.ZodType<DataAndMessageResponse<Memo>> =
  z.object({
    data: MemoSchema,
    message: z.string(),
  });

const UpdateMemoResponseSchema: z.ZodType<DataAndMessageResponse<Memo>> =
  z.object({
    data: MemoSchema,
    message: z.string(),
  });

const DeleteMemoResponseSchema: z.ZodType<MessageResponse> = z.object({
  message: z.string(),
});

export function createMemoModule(httpClient: HttpClient) {
  return {
    list: async () => {
      const response = await httpClient.get<typeof ListMemoResponseSchema>(
        "/api/v1/memos",
        {
          schema: ListMemoResponseSchema,
        },
      );
      return response.data;
    },

    get: async (id: number) => {
      const response = await httpClient.get<typeof GetMemoResponseSchema>(
        `/api/v1/memos/${id}`,
        {
          schema: GetMemoResponseSchema,
        },
      );
      return response.data;
    },

    create: async (data: CreateMemoDto) => {
      const response = await httpClient.post<typeof CreateMemoResponseSchema>(
        "/api/v1/memos",
        {
          data,
          schema: CreateMemoResponseSchema,
        },
      );
      return response.data;
    },

    update: async (id: number, data: UpdateMemoDto) => {
      const response = await httpClient.patch<typeof UpdateMemoResponseSchema>(
        `/api/v1/memos/${id}`,
        {
          data,
          schema: UpdateMemoResponseSchema,
        },
      );
      return response.data;
    },

    remove: async (id: number) => {
      const response = await httpClient.delete<typeof DeleteMemoResponseSchema>(
        `/api/v1/memos/${id}`,
        {
          schema: DeleteMemoResponseSchema,
        },
      );
      return response;
    },
  };
}
